import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { Box } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

import type { SearchFilterPresetKey } from '@/components/NavBar/SearchTypeFilter/SearchTypeFilter'
import { presetsToTypes } from '@/components/NavBar/SearchTypeFilter/SearchTypeFilter'
import { SearchWrap, StyledText } from '@/components/NavBar/styles'
import {
	STRUCTURE_SEARCH_PAGE_SIZE,
	useAppContext,
	useDebounce,
	useStructureSearch,
} from '@/hooks'
import type { StructureSearchOption } from '@/hooks/useStructureSearch'
import { useSelectedSystem } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { FullTextSearchDto } from '@/rtkApi/mddApiSliceGenerated'

import { Results } from './components/Results'

const mapToResult = (item: StructureSearchOption): FullTextSearchDto => ({
	id: item.id,
	name: item.name,
	code: item.code,
	type: item.type,
	path: item.fullPath,
})

export const Search = () => {
	const { t } = useAppContext()
	const { selectedSystemId } = useSelectedSystem()
	const [search, setSearch] = useState('')
	const [focused, setFocused] = useState(false)
	const [selectedPresets, setSelectedPresets] = useState<SearchFilterPresetKey[]>([])
	const [accumulatedResults, setAccumulatedResults] = useState<FullTextSearchDto[]>([])
	const [pageNumber, setPageNumber] = useState(1)
	const [hasMore, setHasMore] = useState(false)
	const [loadingMore, setLoadingMore] = useState(false)

	const query = useDebounce(search, 300)
	const searchTypes = presetsToTypes(selectedPresets)
	const startFromId = selectedSystemId ? Number(selectedSystemId) : undefined

	const { loadPage, isFetching } = useStructureSearch({
		types: searchTypes,
		pageSize: STRUCTURE_SEARCH_PAGE_SIZE,
		startFromId,
	})

	const loadFirstPage = useCallback(async () => {
		if (!query || query.length < 2) {
			setAccumulatedResults([])
			return
		}
		setAccumulatedResults([])
		setPageNumber(1)
		const options = await loadPage(query, 1)
		const results = options.map(mapToResult)
		setAccumulatedResults(results)
		setHasMore(options.length >= STRUCTURE_SEARCH_PAGE_SIZE)
	}, [query, loadPage])

	useEffect(() => {
		if (query && query.length >= 2) {
			loadFirstPage()
		} else {
			setAccumulatedResults([])
			setHasMore(false)
		}
	}, [query, selectedPresets, loadFirstPage, startFromId])

	const loadMore = useCallback(async () => {
		if (!query || query.length < 2 || loadingMore || !hasMore) return
		setLoadingMore(true)
		const nextPage = pageNumber + 1
		const options = await loadPage(query, nextPage)
		const results = options.map(mapToResult)
		setAccumulatedResults((prev) => [...prev, ...results])
		setPageNumber(nextPage)
		setHasMore(options.length >= STRUCTURE_SEARCH_PAGE_SIZE)
		setLoadingMore(false)
	}, [query, pageNumber, hasMore, loadingMore, loadPage])

	const results =
		query && query.length >= 2 ? accumulatedResults : undefined

	return (
		<SearchWrap>
			<StyledText
				inputIcon={faSearch}
				value={search}
				onChange={(v) => setSearch(v as string)}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				clearable
				name="NavBarSearch"
				placeholder={t('NAVBAR_GO_TO_PLACEHOLDER')}
			/>
			{!search && (
				<Box
					component="span"
					sx={{
						pointerEvents: 'none',
						position: 'absolute',
						right: 12,
						top: '50%',
						transform: 'translateY(-50%)',
						borderRadius: '4px',
						border: '1px solid rgba(255,255,255,0.25)',
						px: 0.75,
						py: 0.25,
						fontSize: 10,
						color: 'rgba(255,255,255,0.55)',
					}}
				>
					⌘K
				</Box>
			)}

			{focused && (results || isFetching) && (
				<Results
					results={results}
					loading={isFetching && accumulatedResults.length === 0}
					loadingMore={loadingMore}
					hasMore={hasMore}
					onLoadMore={loadMore}
					selectedPresets={selectedPresets}
					onFilterPresetToggle={setSelectedPresets}
				/>
			)}
		</SearchWrap>
	)
}
